import { type AxiosPromise, isAxiosError, isCancel } from 'axios';
import { type Ref, type ShallowRef, ref, shallowRef } from 'vue';

import { type CancellablePromise } from './createCancellablePromise';
import { useLoadable } from './useLoadable';
import { useMeta } from './useMeta';
import { useStatusCode } from './useStatusCode';

type EntitiesResponse<TData, TMeta> = {
  data: TData[];
  message: string;
  meta?: TMeta;
  status: number;
};

// eslint-disable-next-line max-lines-per-function
export const useGetEntities = <TArgs extends unknown[], TData, TMeta = never, Shallow extends boolean = true>(
  handler: (...args: TArgs) => CancellablePromise<AxiosPromise<EntitiesResponse<TData, TMeta>>>,
  initialMeta?: TMeta,
  initialState: TData[] = [],
  options?: {
    shallow: Shallow;
  },
) => {
  const { shallow = true } = options ?? {};

  const state = shallow ? shallowRef(initialState) : ref(initialState);

  function resetState() {
    state.value = initialState;
  }

  const { abortController, cancel, hasLoaded, isLoading, reset: resetLoadable } = useLoadable();
  const { reset: resetStatusCode, statusCode } = useStatusCode();
  const { meta, reset: resetMeta } = useMeta<TMeta>(initialMeta);

  async function getEntities(...args: TArgs) {
    cancel();

    const { abortController: controller, promise } = handler(...args);

    abortController.value = controller;

    try {
      isLoading.value = true;

      const response = await promise();

      state.value = response.data.data;
      meta.value = response.data.meta;
      statusCode.value = response.status;

      hasLoaded.value = true;
      isLoading.value = false;

      return response;
    } catch (error) {
      if (!isCancel(error)) {
        isLoading.value = false;
      }

      if (isAxiosError<EntitiesResponse<TData, TMeta>>(error)) {
        meta.value = error.response?.data.meta;
        statusCode.value = error.status;
      }

      throw error;
    }
  }

  async function requireEntities(...args: TArgs) {
    if (!isLoading.value && !hasLoaded.value) {
      await getEntities(...args);
    }

    return true;
  }

  function reset() {
    resetLoadable();
    resetState();
    resetStatusCode();
    resetMeta();
  }

  return {
    cancel,
    entities: state as Shallow extends true ? ShallowRef<TData[]> : Ref<TData[]>,
    getEntities,
    hasLoaded,
    isLoading,
    meta,
    requireEntities,
    reset,
    statusCode,
  };
};
